// src/views/smd-analytics/SMDAnalytics.js

import React, { useState, useEffect } from "react";
import { CCard, CCardHeader, CCardBody, CRow, CCol, CButton, CSpinner, CBadge, CProgress } from "@coreui/react";
import { CChartBar, CChartDoughnut, CChartLine, CChartPie, CChartPolarArea, CChartRadar } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import { cilCalendar, cilChart } from "@coreui/icons";
import { DateRangePicker } from "react-date-range";
import Select from "react-select";
import { format, subMonths } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { getSMDSummary } from "src/services/Analytics";
import { getAll as getAllUsers } from "src/services/Users";
import { CDataTable } from "@coreui/react";

const SMDAnalytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [salesOptions, setSalesOptions] = useState([]);
  const [selectedSales, setSelectedSales] = useState(null);

  // Date range state
  const [dateRange, setDateRange] = useState([
    {
      startDate: searchParams.get("startDate") ? new Date(searchParams.get("startDate")) : subMonths(new Date(), 1),
      endDate: searchParams.get("endDate") ? new Date(searchParams.get("endDate")) : new Date(),
      key: "selection",
    },
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Fetch sales people
  useEffect(() => {
    const fetchSalesPeople = async () => {
      setLoadingSales(true);
      try {
        const res = await getAllUsers();
        const options = res.users.map((user) => ({
          value: user.id,
          label: user.name,
        }));
        setSalesOptions(options);

        // Set initial selected sales from URL if available
        const salesId = searchParams.get("salesId");
        if (salesId) {
          const selected = options.find((opt) => opt.value === parseInt(salesId));
          setSelectedSales(selected);
        }
      } catch (error) {
        console.error("Error loading sales people:", error);
      } finally {
        setLoadingSales(false);
      }
    };

    fetchSalesPeople();
  }, []);

  // Fetch analytics data
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedSales) return;

      setLoading(true);
      try {
        const startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
        const endDate = format(dateRange[0].endDate, "yyyy-MM-dd");

        const res = await getSMDSummary({
          smdId: selectedSales.value,
          dateFrom: startDate,
          dateTo: endDate,
        });

        if (res.success) {
          setAnalyticsData(res.data);
        }
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedSales, dateRange]);

  const handleSalesChange = (selectedOption) => {
    setSelectedSales(selectedOption);
    // Update URL param
    const params = {};
    searchParams.forEach((value, key) => (params[key] = value));
    if (selectedOption) {
      params.salesId = selectedOption.value;
    } else {
      delete params.salesId;
    }
    setSearchParams(params);
  };

  const handleDateApply = () => {
    setShowDatePicker(false);
    // Update URL params
    const params = {};
    searchParams.forEach((value, key) => (params[key] = value));
    params.startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
    params.endDate = format(dateRange[0].endDate, "yyyy-MM-dd");
    setSearchParams(params);
  };

  // Helper to render percentage bars
  const renderPercentageBar = (value) => (
    <div className="progress-container">
      <CProgress value={value} max={100} color={value >= 80 ? "success" : value >= 50 ? "warning" : "danger"} />
      <span className="progress-value">{value}%</span>
    </div>
  );

  return (
    <div className="smd-analytics">
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol xs={12} md={6} className="mb-3 mb-md-0">
              <h4 className="mb-0 d-flex align-items-center">
                <CIcon icon={cilChart} className="me-2" />
                SMD Performance Analytics
              </h4>
            </CCol>

            <CCol xs={12} md={6}>
              <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-md-center gap-2">
                <div style={{ minWidth: "220px" }}>
                  <Select
                    options={salesOptions}
                    value={selectedSales}
                    onChange={handleSalesChange}
                    placeholder="Select SMD"
                    isClearable
                    isLoading={loadingSales}
                  />
                </div>

                <CButton color="primary" onClick={() => setShowDatePicker(!showDatePicker)} disabled={!selectedSales}>
                  <CIcon icon={cilCalendar} className="me-2" />
                  {format(dateRange[0].startDate, "MMM d, yyyy")} – {format(dateRange[0].endDate, "MMM d, yyyy")}
                </CButton>
              </div>
            </CCol>
          </CRow>
        </CCardHeader>

        {showDatePicker && (
          <CCardBody className="date-picker-container">
            <DateRangePicker
              onChange={(item) => setDateRange([item.selection])}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={dateRange}
              direction="horizontal"
            />
            <div className="text-right mt-3">
              <CButton color="secondary" onClick={() => setShowDatePicker(false)} className="mr-2">
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleDateApply}>
                Apply
              </CButton>
            </div>
          </CCardBody>
        )}

        {loading ? (
          <CCardBody className="text-center">
            <CSpinner />
            <p className="mt-2">Loading analytics data...</p>
          </CCardBody>
        ) : analyticsData && selectedSales ? (
          <CCardBody>
            {/* Summary Cards */}
            <CRow className="mb-4">
              <CCol sm={6} lg={3}>
                <CCard className="text-white bg-info h-100">
                  <CCardBody>
                    <div className="text-value">{analyticsData.summary.total_assigned_stores || 0}</div>
                    <div>Assigned Stores</div>
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol sm={6} lg={3}>
                <CCard className="text-white bg-success h-100">
                  <CCardBody>
                    <div className="text-value">{analyticsData.summary.total_stores_visited || 0}</div>
                    <div>Visited Stores</div>
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol sm={6} lg={3}>
                <CCard className="h-100">
                  <CCardBody>
                    <div>Coverage Rate</div>
                    {renderPercentageBar(analyticsData.summary.coverage_percentage || 0)}
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol sm={6} lg={3}>
                <CCard className="h-100">
                  <CCardBody>
                    <div className="text-value">
                      {analyticsData.summary.avg_visit_duration
                        ? Math.round(analyticsData.summary.avg_visit_duration)
                        : 0}{" "}
                      mins
                    </div>
                    <div>Avg Visit Duration</div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* Charts Row */}
            <CRow className="mb-4">
              <CCol lg={6}>
                <CCard>
                  <CCardHeader>
                    <h5>Visit Statistics by Visibility</h5>
                  </CCardHeader>
                  <CCardBody>
                    <CChartBar
                      data={{
                        labels: analyticsData.visit_stats.map((item) => item.visibility),
                        datasets: [
                          {
                            label: "Visit Count",
                            backgroundColor: "#4e5d97",
                            data: analyticsData.visit_stats.map((item) => item.visit_count),
                          },
                          {
                            label: "Avg Duration (mins)",
                            backgroundColor: "#39f",
                            data: analyticsData.visit_stats.map((item) => Math.round(item.avg_duration)),
                          },
                        ],
                      }}
                      options={{
                        aspectRatio: 1.5,
                        scales: {
                          yAxes: [
                            {
                              ticks: {
                                beginAtZero: true,
                              },
                            },
                          ],
                        },
                      }}
                    />
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol lg={6}>
                <CCard>
                  <CCardHeader>
                    <h5>Performance Trend (Last 6 Months)</h5>
                  </CCardHeader>
                  <CCardBody>
                    <CChartLine
                      data={{
                        labels: analyticsData.performance_trend.map((item) => item.month),
                        datasets: [
                          {
                            label: "Unique Stores Visited",
                            backgroundColor: "rgba(78, 93, 151, 0.1)",
                            borderColor: "#4e5d97",
                            pointBackgroundColor: "#4e5d97",
                            pointHoverBackgroundColor: "#4e5d97",
                            data: analyticsData.performance_trend.map((item) => item.unique_stores_visited),
                          },
                          {
                            label: "Total Visits",
                            backgroundColor: "rgba(57, 153, 255, 0.1)",
                            borderColor: "#39f",
                            pointBackgroundColor: "#39f",
                            pointHoverBackgroundColor: "#39f",
                            data: analyticsData.performance_trend.map((item) => item.total_visits),
                          },
                        ],
                      }}
                      options={{
                        aspectRatio: 1.5,
                        scales: {
                          yAxes: [
                            {
                              ticks: {
                                beginAtZero: true,
                              },
                            },
                          ],
                        },
                      }}
                    />
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* Store Coverage */}
            <CRow className="mb-4">
              <CCol>
                <CCard>
                  <CCardHeader>
                    <h5>Store Coverage by Type</h5>
                  </CCardHeader>
                  <CCardBody>
                    {/* <CDataTable
                      items={analyticsData.store_coverage}
                      fields={[
                        { key: "type", label: "Store Type" },
                        { key: "total_stores", label: "Total Stores" },
                        { key: "visited_stores", label: "Visited Stores" },
                        {
                          key: "coverage_percentage",
                          label: "Coverage %",
                          sorter: false,
                          filter: false,
                          _style: { width: "30%" },
                        },
                      ]}
                      hover
                      striped
                      itemsPerPage={5}
                      scopedSlots={{
                        coverage_percentage: (item) => <td>{renderPercentageBar(item.coverage_percentage || 0)}</td>,
                      }}
                    /> */}
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            {/* Top Stores */}
            <CRow>
              <CCol>
                <CCard>
                  <CCardHeader>
                    <h5>Top Visited Stores</h5>
                  </CCardHeader>
                  <CCardBody>
                    {/* <CDataTable
                      items={analyticsData.top_stores}
                      fields={[
                        { key: "store_name", label: "Store Name" },
                        { key: "visit_count", label: "Visit Count" },
                        {
                          key: "avg_duration",
                          label: "Avg Duration (mins)",
                          sorter: false,
                          filter: false,
                        },
                        {
                          key: "last_visited",
                          label: "Last Visited",
                          sorter: false,
                          filter: false,
                        },
                      ]}
                      hover
                      striped
                      itemsPerPage={5}
                      scopedSlots={{
                        avg_duration: (item) => <td>{Math.round(item.avg_duration)} mins</td>,
                        last_visited: (item) => <td>{format(new Date(item.last_visited), "MMM d, yyyy")}</td>,
                      }}
                    /> */}
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </CCardBody>
        ) : (
          <CCardBody className="text-center">
            {selectedSales ? (
              <p>No analytics data available for the selected period</p>
            ) : (
              <p>Please select an SMD to view analytics</p>
            )}
          </CCardBody>
        )}
      </CCard>
    </div>
  );
};

export default SMDAnalytics;
